import { abortable } from '../async/abort';

export type EventListenable = {
  addEventListener<N extends string, L extends EventListener>(
    type: N,
    listener: L,
    options?: AddEventListenerOptions | boolean
  ): void;
  removeEventListener<N extends string, L extends EventListener>(
    type: N,
    listener: L
  ): void;
};

export type EventNames<T extends EventListenable> = Parameters<
  T['addEventListener']
>[0];
export type EventType<
  T extends EventListenable,
  N extends EventNames<T>,
> = T extends {
  addEventListener(
    type: N,
    listener: (event: infer Evt) => any,
    options?: AddEventListenerOptions | boolean
  ): void;
}
  ? Evt
  : never;

/**
 * Resolves with the next event of a given type.
 *
 * @param target - The event target.
 * @param type - The event type.
 * @param signal - An optional abort signal.
 * @returns The event.
 */
export async function nextEvent<
  T extends EventListenable,
  N extends EventNames<T>,
>(target: T, type: N, signal?: AbortSignal): Promise<EventType<T, N>> {
  let listener: ((event: EventType<T, N>) => void) | null = null;
  return abortable(
    new Promise<EventType<T, N>>((resolve) => {
      listener = resolve;
      target.addEventListener(type, resolve as EventListener, {
        signal,
        once: true,
        passive: true,
      });
    }),
    signal
  ).finally(() => {
    if (listener) {
      target.removeEventListener(type, listener as EventListener);
    }
  });
}
